import { Resolver, useForm } from 'react-hook-form';
import { Button, Input, Modal, Stack, Text } from '@ltvco/refresh-lib/theme';
import { yupResolver } from '@hookform/resolvers/yup';
import { ResponseError } from '@ltvco/refresh-lib/utils';
import { answerModalSchema } from 'utils/formSchemas/formSchemas';
import { ErrorMessageWithIcon } from 'components/shared/ErrorMessageWithIcon';
import { StyledTextArea } from './components';
import { BUMPER_AI_SOURCE, BUMPER_AI_USERNAME } from '../constants';

export interface AnswerFormData {
  data: string;
  name?: string;
}

interface QAEntryModalProps {
  isOpen: boolean;
  title: string;
  type: 'Answer' | 'Question' | 'Comment';
  onClose: () => void;
  onSubmit: (data: AnswerFormData) => Promise<void> | void;
}

export const QAEntryModal = ({
  isOpen,
  title,
  type,
  onClose,
  onSubmit,
}: QAEntryModalProps) => {
  const {
    handleSubmit,
    register,
    clearErrors,
    formState: { errors },
    setValue,
    watch,
    reset,
    setError,
  } = useForm<AnswerFormData>({
    mode: 'onSubmit',
    resolver: yupResolver(
      answerModalSchema,
    ) as unknown as Resolver<AnswerFormData>,
    defaultValues: {
      data: '',
      name: '',
    },
  });
  const data = watch('data');

  const handleChangeData = (event: React.ChangeEvent<HTMLInputElement>) => {
    clearErrors('data');
    setValue('data', event.target.value);
  };

  const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    clearErrors('name');
    setValue('name', event.target.value);
  };

  const handleOnClose = () => {
    clearErrors();
    reset();
    onClose();
  };

  const moderationErrorMessage =
    'The submitted text may contain content that violates our Terms & Conditions. Please update it and try again.';

  const handleOnSubmit = async (data: AnswerFormData) => {
    const normalizedName = data.name?.trim().toLowerCase();
    const bumperAIReservedNames = [
      BUMPER_AI_SOURCE.toLowerCase(),
      BUMPER_AI_USERNAME.toLowerCase(),
    ];

    if (normalizedName && bumperAIReservedNames.includes(normalizedName)) {
      setError('name', {
        message: 'Reserved name. Please use a different username.',
      });
      return;
    }

    try {
      await onSubmit(data);
      handleOnClose();
    } catch (error) {
      // 422 means the content moderation found unwanted content
      if ((error as ResponseError).response?.status === 422) {
        setError('data', {
          message: moderationErrorMessage,
        });
        setError('name', {
          message: moderationErrorMessage,
        });
      }
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleOnClose}
      hideBackdrop
      hasCloseIcon
      sx={{
        '.MuiDialog-paper': {
          width: '444px',
        },
      }}
    >
      <form
        data-testid="answer-modal-form"
        data-cy="answer-modal-form"
        onSubmit={handleSubmit(handleOnSubmit)}
        autoComplete="off"
        aria-label="form"
      >
        <Stack spacing={3}>
          <Text variant="h5" mb={2}>
            {title}
          </Text>
          <Stack spacing={1}>
            <Text variant="h5">{type}</Text>
            <StyledTextArea
              {...register('data')}
              error={Boolean(errors.data)}
              placeholder={`Write your ${type.toLowerCase()} here`}
              onChange={handleChangeData}
            />
            {errors.data && (
              <ErrorMessageWithIcon errorMessage={errors.data.message} />
            )}
          </Stack>
          <Stack spacing={1}>
            <Text variant="h5">
              Name to associate with this {type.toLowerCase()}
            </Text>
            <Input
              {...register('name')}
              error={Boolean(errors.name)}
              sx={{ mt: 0 }}
              onChange={handleChangeName}
            />
            {errors.name && (
              <ErrorMessageWithIcon errorMessage={errors.name.message} />
            )}
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Button
              variant="text"
              onClick={handleOnClose}
              sx={{ textDecoration: 'underline', fontWeight: 'normal' }}
            >
              Cancel
            </Button>
            <Button variant="contained" type="submit" disabled={!data}>
              Post {type}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Modal>
  );
};
